import {
  completeKycSession,
  getKycSession,
  retryKycSession,
  startKycSession
} from '@/api/kyc';
import {
  KYC_V2_NEXT_ACTION,
  buildKycV2RedirectUrl,
  clearKycV2Context,
  clearKycV2ReturnQuery,
  getKycV2LaunchUrl,
  isKycV2Return,
  readKycV2Context,
  saveKycV2Context,
  toV2AuthResult
} from '@/utils/kycV2';

export async function startKycV2Flow(vm, options = {}) {
  const returnPath = options.returnPath || vm.$route.path;
  const redirectUrl = buildKycV2RedirectUrl(returnPath, options.returnQuery || {});
  const res = await startKycSession({
    channel: 'H5',
    platform: 'WEB',
    redirectUrl,
    language: vm.$i18n && vm.$i18n.locale
  });
  if (res.code !== 'Success') {
    vm.$toast(res.message || vm.$t('systemError'));
    return false;
  }

  return handleKycV2View(vm, res.data || {}, {
    ...options,
    returnPath
  });
}

export async function completeKycV2Flow(vm, options = {}) {
  if (!isKycV2Return(vm.$route)) {
    return null;
  }
  const context = readKycV2Context();
  const sessionId = context.sessionId;
  if (!sessionId) {
    clearKycV2ReturnQuery(vm.$router, vm.$route);
    return null;
  }

  const res = await completeKycSession(sessionId, {}, { _showLoading: false });
  if (res.code !== 'Success') {
    if (!options.silent) {
      vm.$toast(res.message || vm.$t('systemError'));
      clearKycV2ReturnQuery(vm.$router, vm.$route);
    }
    if (options.throwOnError) {
      throw new Error(res.message || 'Complete KYC session failed');
    }
    return null;
  }
  const view = await queryKycV2Result(sessionId, res.data || {}, options);
  saveKycV2Context({
    status: view && view.status,
    nextAction: view && view.nextAction
  });
  if ([
    KYC_V2_NEXT_ACTION.UPLOAD_DOCUMENT,
    KYC_V2_NEXT_ACTION.OPEN_URL,
    KYC_V2_NEXT_ACTION.RUN_SDK,
    KYC_V2_NEXT_ACTION.POLL_RESULT
  ].includes(view.nextAction)) {
    handleKycV2View(vm, view, context);
    return null;
  }
  return view;
}

async function queryKycV2Result(sessionId, fallbackView = {}, options = {}) {
  const res = await getKycSession(sessionId, { _showLoading: false });
  if (res.code !== 'Success' && options.throwOnError) {
    throw new Error(res.message || 'Query KYC session failed');
  }
  if (res.code !== 'Success') return fallbackView;
  return res.data || fallbackView;
}

export async function retryKycV2Flow(vm) {
  const context = readKycV2Context();
  const sessionId = context.sessionId;
  if (!sessionId) {
    vm.$toast(vm.$t('systemError'));
    return false;
  }
  const res = await retryKycSession(sessionId);
  if (res.code !== 'Success') {
    vm.$toast(res.message || vm.$t('systemError'));
    return false;
  }
  return handleKycV2View(vm, res.data || {}, context);
}

export function openKycV2ResultPopup(vm, view = {}) {
  const verifyResult = toV2AuthResult(view);
  if (!verifyResult) return false;
  vm.$refs.authResultPopup.open({
    authType: 'ocr',
    verifyResult
  });
  return true;
}

export function clearFinishedKycV2(vm) {
  clearKycV2Context();
  clearKycV2ReturnQuery(vm.$router, vm.$route);
}

export function handleKycV2View(vm, view = {}, options = {}) {
  if (view.nextAction === KYC_V2_NEXT_ACTION.DONE || view.status === 'SUCCESS') {
    return true;
  }

  const sessionId = view.sessionId || options.sessionId;
  const launchUrl = getKycV2LaunchUrl(view);
  saveKycV2Context({
    ...options,
    sessionId,
    status: view.status,
    nextAction: view.nextAction,
    launchUrl
  });

  if (view.nextAction === KYC_V2_NEXT_ACTION.UPLOAD_DOCUMENT) {
    showKycRedirectTip(vm, 'kycV2.enterKtp');
    window.setTimeout(() => {
      vm.$router.push({
        path: '/kyc/ktp-camera',
        query: { sessionId }
      });
    }, 800);
    return false;
  }

  if (
    [KYC_V2_NEXT_ACTION.OPEN_URL, KYC_V2_NEXT_ACTION.RUN_SDK].includes(view.nextAction) &&
    launchUrl
  ) {
    showKycRedirectTip(vm, 'kycV2.enterFace');
    window.setTimeout(() => {
      window.location.href = launchUrl;
    }, 800);
    return false;
  }

  if (view.nextAction === KYC_V2_NEXT_ACTION.POLL_RESULT) {
    return false;
  }

  openKycV2ResultPopup(vm, view);
  return false;
}

function showKycRedirectTip(vm, key) {
  if (!vm || !vm.$toast) return;
  vm.$toast({
    message: vm.$t ? vm.$t(key) : '',
    duration: 800,
    forbidClick: true
  });
}
